import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslations } from 'use-intl';
import { useQuery } from '@tanstack/react-query';
import {
  Header,
  HeaderBackButton,
  HeaderCopyButton,
  HeaderSidebarTrigger,
  HeaderUserMenu,
} from '@/app/ui/components/header';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from '@/app/ui/components/breadcrumb';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  SparklesIcon,
  ArrowRight02Icon,
  ChartDownIcon,
  ChartUpIcon,
  DocumentValidationIcon,
  PlusSignIcon,
  AlertSquareIcon,
  Refresh01Icon,
} from '@hugeicons/core-free-icons';
import { MainScrollArea, MainHeader, MainContainer, MainFrames } from '@/app/ui/components/main';
import { Button } from '@/app/ui/components/button';
import { H1, H2, H3 } from '@/app/ui/components/heading';
import { useContext, useState } from 'react';
import { Tabs, TabsList, TabsPanel, TabsTab } from '@/app/ui/components/tabs';
import { useSetAtom } from 'jotai';
import {
  useGetLearningsQuery,
  useGenerateLearningsMutation,
} from '@/app/hooks/queries/useLearnings';
import { AppContext } from '@/app/appContext';
import { createLearningOpenAtom } from '@/app/components/features/learnings/store';
import { CreateLearning } from '@/app/components/features/learnings/CreateLearning';
import { Badge } from '@/app/ui/components/badge';
import { Frame, FramePanel } from '@/app/ui/components/frame';
import { Skeleton } from '@/app/ui/components/skeleton';
import {
  OrgTenderFeedbacksProjectTypeEnum,
  ProjectTypeLabelsEnum,
  SummarizationTopicStatus,
} from '@/app/utils/constants';
import {
  LearningsFramePanel,
  LearningsFramePanelBadge,
  LearningsFramePanelCount,
  LearningsFramePanelHelpIcon,
  LearningsFramePanelText,
} from '@/app/components/features/learnings/LearningsFramePanel';
import { Icons } from '@/app/ui/components/icons';
import { Alert, AlertAction, AlertDescription, AlertTitle } from '@/app/ui/components/alert';
import { ClientsIcon, LearningsIcon } from '@/app/components/shared/EmptyIcons';
import { Progress, ProgressTrack, ProgressValue } from '@/app/ui/components/progress';
import { Spinner } from '@/app/ui/components/spinner';
import { useOrganizationTenderFeedbacks } from '@/app/hooks/queries/useStandaloneTenderFeedbacks';
import { LearningInsight, StandaloneTenderFeedback } from '@/app/types/Dtos/response';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyMedia,
  EmptyTitle,
} from '@/app/ui/components/empty';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogPopup,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/app/ui/components/alert-dialog';
import { Tooltip, TooltipPopup, TooltipTrigger } from '@/app/ui/components/tooltip';
import { getOrgTenderFeedbackIndexingStatus } from '@/app/services/tenderFeedbacksService';
import { queryKeys } from '@/app/utils/constants';

const tabs = {
  TENDER: 'tender',
  ALL: 'all',
} as const;

type TabValue = (typeof tabs)[keyof typeof tabs];

export const Route = createFileRoute('/_authed/(organization)/_layout/learnings/')({
  component: RouteComponent,
});

function RouteComponent() {
  const t = useTranslations('pages.learnings');
  const tGeneral = useTranslations('general');
  const [tab, setTab] = useState<TabValue>(tabs.TENDER);
  const [rerunAnalysisOpen, setRerunAnalysisOpen] = useState(false);
  const { userMeta } = useContext(AppContext);
  const organizationId = userMeta?.organizationId ?? '';
  const { data: tenderFeedbacks, isLoading: isLoadingFeedbacks } =
    useOrganizationTenderFeedbacks(organizationId);
  const { data, isLoading } = useGetLearningsQuery({
    organizationId,
    enabled: !!organizationId,
  });

  const { mutateAsync: generate, isPending: isGenerating } =
    useGenerateLearningsMutation(organizationId);

  const indexingStatusQuery = useQuery({
    queryKey: [queryKeys.INDEXING_STATUS, 'tender_feedback', organizationId],
    queryFn: async () => {
      const response = await getOrgTenderFeedbackIndexingStatus(organizationId);
      return response?.data ?? null;
    },
    enabled: !!organizationId,
    refetchInterval: (query) => {
      if (query.state.data?.overallProgressPercentage === 100) return false;
      return 15 * 1000;
    },
  });

  const isFullyIndexed = indexingStatusQuery.data?.overallProgressPercentage === 100;

  const insights = data?.insights ?? [];
  const infraItem = insights.find(
    (i) =>
      i.projectType === OrgTenderFeedbacksProjectTypeEnum.Infrastructure ||
      i.projectType === ProjectTypeLabelsEnum.Infrastructure,
  );
  const buildingItem = insights.find(
    (i) =>
      i.projectType === OrgTenderFeedbacksProjectTypeEnum.Building ||
      i.projectType === ProjectTypeLabelsEnum.Building,
  );
  const infraProcessing = infraItem?.status === SummarizationTopicStatus.IN_PROGRESS;
  const buildingProcessing = buildingItem?.status === SummarizationTopicStatus.IN_PROGRESS;
  const anyProcessing = infraProcessing || buildingProcessing;

  const avgPercentage = ((infraItem?.percentage ?? 0) + (buildingItem?.percentage ?? 0)) / 2;

  const hasAnyData = infraItem || buildingItem;
  const showContent = anyProcessing ? false : !isLoadingFeedbacks && !isLoading && hasAnyData;

  const noFeedbacks = tenderFeedbacks && tenderFeedbacks.length === 0;

  return (
    <>
      <CreateLearning />
      <Header>
        <HeaderBackButton />
        <HeaderSidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbPage>
                <HugeiconsIcon icon={SparklesIcon} />
                {t('title')}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <HeaderUserMenu />
        <HeaderCopyButton />
      </Header>
      <MainScrollArea>
        {showContent ? (
          <>
            <MainHeader className="max-md:flex">
              <H1 className="max-sm:hidden">{t('title')}</H1>
              <div className="grid grid-cols-2 sm:flex max-sm:w-full items-center gap-1.5">
                <Button
                  className="max-sm:w-full"
                  variant="secondary"
                  kind="with-icon"
                  nativeButton={false}
                  render={
                    <Link to={'/knowledge-base/new-feedback'}>
                      <HugeiconsIcon icon={PlusSignIcon} />
                      {t('button')}
                    </Link>
                  }
                />
                {!isFullyIndexed ? (
                  <Tooltip>
                    <TooltipTrigger
                      delay={0}
                      render={
                        <Button kind="with-icon" variant="secondary" disabled>
                          <HugeiconsIcon icon={Refresh01Icon} />
                          {t('regenerate')}
                        </Button>
                      }
                    />
                    <TooltipPopup>
                      {t('indexingTooltip', {
                        percentage: (
                          indexingStatusQuery.data?.overallProgressPercentage ?? 0
                        ).toFixed(0),
                      })}
                    </TooltipPopup>
                  </Tooltip>
                ) : (
                  <AlertDialog open={rerunAnalysisOpen} onOpenChange={setRerunAnalysisOpen}>
                    <AlertDialogTrigger
                      render={
                        <Button kind="with-icon" variant="secondary">
                          <HugeiconsIcon icon={Refresh01Icon} />
                          {t('regenerate')}
                        </Button>
                      }
                    />
                    <AlertDialogPopup>
                      <AlertDialogTitle>{tGeneral('reRunAnalysis')}?</AlertDialogTitle>
                      <AlertDialogDescription>
                        {tGeneral('reRunAnalysisDescription')}
                      </AlertDialogDescription>
                      <AlertDialogFooter>
                        <AlertDialogCancel disabled={isGenerating} />
                        <Button
                          onClick={async () => {
                            await generate();
                            setRerunAnalysisOpen(false);
                          }}
                          pending={isGenerating}
                        >
                          {tGeneral('reRunAnalysis')}
                        </Button>
                      </AlertDialogFooter>
                    </AlertDialogPopup>
                  </AlertDialog>
                )}
              </div>
            </MainHeader>
            <Tabs
              value={tab}
              onValueChange={(value) => setTab(value as TabValue)}
              className="flex flex-col grow min-h-0 pt-2"
            >
              <TabsList className={'pl-x'}>
                <TabsTab value={tabs.TENDER}>
                  {t('tab2')} <Badge size="xs">{t('tab2Badge')}</Badge>
                </TabsTab>
              </TabsList>
              <MainContainer>
                <TabsPanel value={tabs.TENDER}>
                  <ActivityOverview />
                  <PortfolioInsights
                    infraItem={infraItem}
                    buildingItem={buildingItem}
                    isLoading={isLoading}
                  />
                  <ClientInsights
                    isFullyIndexed={isFullyIndexed}
                    indexingPercentage={indexingStatusQuery.data?.overallProgressPercentage ?? 0}
                  />
                </TabsPanel>
              </MainContainer>
            </Tabs>
          </>
        ) : (
          <>
            {(isLoading || isLoadingFeedbacks) && <Spinner kind="inset" />}
            {!isLoading && !isLoadingFeedbacks && (
              <Empty>
                <EmptyMedia>
                  <LearningsIcon />
                </EmptyMedia>
                <EmptyContent>
                  <EmptyTitle>{t('empty.title')}</EmptyTitle>
                  {anyProcessing ? (
                    <Progress value={+avgPercentage.toFixed(0)} max={100}>
                      <EmptyDescription>
                        {t('analyzing')} - <ProgressValue />
                      </EmptyDescription>
                      <ProgressTrack className="md:absolute md:bottom-0 md:rounded-none md:left-0 w-full" />
                    </Progress>
                  ) : (
                    <>
                      <EmptyDescription>
                        {noFeedbacks ? t('empty.descriptionNoFeedbacks') : t('empty.description')}
                      </EmptyDescription>
                      {noFeedbacks ? (
                        <Button
                          kind="with-icon"
                          nativeButton={false}
                          render={
                            <Link to={'/knowledge-base/new-feedback'}>
                              <HugeiconsIcon icon={PlusSignIcon} />
                              {t('empty.buttonNoFeedbacks')}
                            </Link>
                          }
                        />
                      ) : !isFullyIndexed ? (
                        <Tooltip>
                          <TooltipTrigger
                            delay={0}
                            render={
                              <Button kind="with-icon" disabled>
                                <HugeiconsIcon icon={SparklesIcon} />
                                {t('empty.button')}
                              </Button>
                            }
                          />
                          <TooltipPopup>
                            {t('indexingTooltip', {
                              percentage: (
                                indexingStatusQuery.data?.overallProgressPercentage ?? 0
                              ).toFixed(0),
                            })}
                          </TooltipPopup>
                        </Tooltip>
                      ) : (
                        <Button kind="with-icon" onClick={() => generate()} pending={isGenerating}>
                          <HugeiconsIcon icon={SparklesIcon} />
                          {t('empty.button')}
                        </Button>
                      )}
                    </>
                  )}
                </EmptyContent>
              </Empty>
            )}
          </>
        )}
      </MainScrollArea>
    </>
  );
}

function ActivityOverview() {
  const t = useTranslations('pages.learnings');
  const { userMeta } = useContext(AppContext);
  const organizationId = userMeta?.organizationId ?? '';
  const { data, isLoading, error } = useGetLearningsQuery({
    organizationId,
    enabled: !!organizationId,
  });
  const total = data?.stats.totalUploaded ?? 0;

  return (
    <section>
      <H2 className="mb-3">{t('section1.title')}</H2>
      <Frame stackedPanels className="@2xl:grid-cols-3">
        <LearningsFramePanel>
          <LearningsFramePanelText>
            <LearningsFramePanelCount count={total} isLoading={isLoading} />
            {t('section1.1')}
            <LearningsFramePanelHelpIcon>{t('section1.1Tooltip')}</LearningsFramePanelHelpIcon>
          </LearningsFramePanelText>
          <LearningsFramePanelBadge className="text-primary-11">
            <Icons.evaluations />
          </LearningsFramePanelBadge>
        </LearningsFramePanel>
        <LearningsFramePanel>
          <LearningsFramePanelText>
            <LearningsFramePanelCount count={data?.stats.won ?? 0} isLoading={isLoading} />
            {t('section1.2')}
            <LearningsFramePanelHelpIcon>{t('section1.2Tooltip')}</LearningsFramePanelHelpIcon>
          </LearningsFramePanelText>
          <LearningsFramePanelBadge className="text-success">
            <HugeiconsIcon icon={DocumentValidationIcon} />
          </LearningsFramePanelBadge>
        </LearningsFramePanel>
        <LearningsFramePanel>
          <LearningsFramePanelText>
            <LearningsFramePanelCount count={data?.stats.lost ?? 0} isLoading={isLoading} />
            {t('section1.3')}
            <LearningsFramePanelHelpIcon>{t('section1.3Tooltip')}</LearningsFramePanelHelpIcon>
          </LearningsFramePanelText>
          <LearningsFramePanelBadge className="text-destructive">
            <Icons.documentXIcon />
          </LearningsFramePanelBadge>
        </LearningsFramePanel>
      </Frame>
      {!isLoading && total < 10 ? (
        <Alert variant="warning" className="mt-3">
          <HugeiconsIcon icon={AlertSquareIcon} />
          <AlertTitle>{t('warningTitle')}</AlertTitle>
          <AlertDescription>{t('warningDescription')}</AlertDescription>
          <AlertAction>
            <Button
              nativeButton={false}
              variant="secondary"
              render={<Link to={'/knowledge-base/new-feedback'}>{t('warningAction')}</Link>}
            />
          </AlertAction>
        </Alert>
      ) : null}
    </section>
  );
}

function PortfolioInsights({
  infraItem,
  buildingItem,
  isLoading,
}: {
  infraItem: LearningInsight | undefined;
  buildingItem: LearningInsight | undefined;
  isLoading: boolean;
}) {
  const t = useTranslations('pages.learnings');
  const { userMeta } = useContext(AppContext);
  const organizationId = userMeta?.organizationId ?? '';
  const { data: tenderFeedbacks } = useOrganizationTenderFeedbacks(organizationId);

  return (
    <section>
      <H2 className="mb-3 mt-5">{t('section2.title')}</H2>
      <MainFrames className="@7xl:grid-cols-2">
        <InfrastructureFrame
          item={infraItem}
          tenderFeedbacks={tenderFeedbacks}
          isLoading={isLoading}
        />
        <BuildingFrame
          item={buildingItem}
          tenderFeedbacks={tenderFeedbacks}
          isLoading={isLoading}
        />
      </MainFrames>
    </section>
  );
}

function InfrastructureFrame({
  item,
  tenderFeedbacks,
  isLoading,
}: {
  item: LearningInsight | undefined;
  tenderFeedbacks: StandaloneTenderFeedback[] | undefined;
  isLoading: boolean;
}) {
  const t = useTranslations('pages.learnings');
  const tGeneral = useTranslations('general');

  const infraFeedbacks = tenderFeedbacks?.filter(
    (fb) => fb.projectType === OrgTenderFeedbacksProjectTypeEnum.Infrastructure,
  );
  const increasingItems = item?.statements.filter((s) => s.changeDirection === 'improvement');
  const decreasingItems = item?.statements.filter((s) => s.changeDirection === 'worsening');
  const hasData = item?.statements && item.statements.length > 0;

  return (
    <Frame
      className={hasData ? 'cursor-pointer' : 'cursor-auto'}
      render={
        <Link
          to={'/learnings/portfolio/$projectType'}
          params={{ projectType: ProjectTypeLabelsEnum.Infrastructure }}
          onClick={(e) => {
            if (!hasData) return e.preventDefault();
          }}
        />
      }
    >
      <FramePanel className="h-38 grid place-items-center">
        {isLoading ? (
          <div className="flex items-center gap-3 flex-col">
            <Skeleton className="w-14 h-8" />
            <Skeleton className="w-32 h-4" />
          </div>
        ) : (
          <div className="tabular-nums">
            <div className="flex items-center justify-center gap-2">
              <span className="text-[2.4rem] font-medium block">
                {item?.statements.length ?? 0}
              </span>
              <div className="flex flex-col gap-0.5">
                <div className="flex items-center gap-1 text-sm leading-tight font-medium">
                  <HugeiconsIcon icon={ChartUpIcon} className="size-4 text-green-11" />{' '}
                  {increasingItems?.length ?? 0}
                  <span className="sr-only">{t('improvement')}</span>
                </div>
                <div className="flex items-center gap-1 text-sm leading-tight font-medium">
                  <HugeiconsIcon icon={ChartDownIcon} className="size-4 text-red-11" />{' '}
                  {decreasingItems?.length ?? 0}
                  <span className="sr-only"> {t('worsening')}</span>
                </div>
              </div>
            </div>
            <p className="text-sm">{t('section2.found')}</p>
          </div>
        )}
      </FramePanel>
      <div className="flex max-sm:flex-col p-2 pb-1.25 sm:items-center gap-2 min-h-14.75 md:min-h-15.75">
        <div>
          <H3>{t('projectLearnings', { type: tGeneral('projectType.Infrastructure') })}</H3>
          <p className="text-muted mt-0.5 text-sm lowercase first-letter:capitalize">
            {t.rich('section2.description', {
              type: tGeneral('projectType.Infrastructure'),
              count: infraFeedbacks?.length ?? 0,
              span: (chunks) => <span className="underline">{chunks}</span>,
            })}
          </p>
        </div>
        {!hasData ? null : (
          <HugeiconsIcon
            icon={ArrowRight02Icon}
            className="invisible group-hover:visible size-6 ml-auto"
          />
        )}
      </div>
    </Frame>
  );
}

function BuildingFrame({
  item,
  tenderFeedbacks,
  isLoading,
}: {
  item: LearningInsight | undefined;
  tenderFeedbacks: StandaloneTenderFeedback[] | undefined;
  isLoading: boolean;
}) {
  const t = useTranslations('pages.learnings');
  const tGeneral = useTranslations('general');

  const buildingFeedbacks = tenderFeedbacks?.filter(
    (fb) => fb.projectType === OrgTenderFeedbacksProjectTypeEnum.Building,
  );
  const increasingItems = item?.statements.filter((s) => s.changeDirection === 'improvement');
  const decreasingItems = item?.statements.filter((s) => s.changeDirection === 'worsening');
  const hasData = item?.statements && item.statements.length > 0;

  return (
    <Frame
      className={hasData ? 'cursor-pointer' : 'cursor-auto'}
      render={
        <Link
          to={'/learnings/portfolio/$projectType'}
          params={{ projectType: ProjectTypeLabelsEnum.Building }}
          onClick={(e) => {
            if (!hasData) return e.preventDefault();
          }}
        />
      }
    >
      <FramePanel className="h-38 grid place-items-center">
        {isLoading ? (
          <div className="flex items-center gap-3 flex-col">
            <Skeleton className="w-14 h-8" />
            <Skeleton className="w-32 h-4" />
          </div>
        ) : (
          <div className="tabular-nums">
            <div className="flex items-center justify-center gap-2">
              <span className="text-[2.4rem] font-medium block">
                {item?.statements.length ?? 0}
              </span>
              <div className="flex flex-col gap-0.5">
                <div className="flex items-center gap-1 text-sm leading-tight font-medium">
                  <HugeiconsIcon icon={ChartUpIcon} className="size-4 text-green-11" />{' '}
                  {increasingItems?.length ?? 0}
                  <span className="sr-only">{t('improvement')}</span>
                </div>
                <div className="flex items-center gap-1 text-sm leading-tight font-medium">
                  <HugeiconsIcon icon={ChartDownIcon} className="size-4 text-red-11" />{' '}
                  {decreasingItems?.length ?? 0}
                  <span className="sr-only"> {t('worsening')}</span>
                </div>
              </div>
            </div>
            <p className="text-sm">{t('section2.found')}</p>
          </div>
        )}
      </FramePanel>
      <div className="flex max-sm:flex-col p-2 pb-1.25 sm:items-center gap-2 min-h-14.75 md:min-h-15.75">
        <div>
          <H3>{t('projectLearnings', { type: tGeneral('projectType.Building') })}</H3>
          <p className="text-muted mt-0.5 text-sm lowercase first-letter:capitalize">
            {t.rich('section2.description', {
              type: tGeneral('projectType.Building'),
              count: buildingFeedbacks?.length ?? 0,
              span: (chunks) => <span className="underline">{chunks}</span>,
            })}
          </p>
        </div>
        {!hasData ? null : (
          <HugeiconsIcon
            icon={ArrowRight02Icon}
            className="invisible group-hover:visible size-6 ml-auto"
          />
        )}
      </div>
    </Frame>
  );
}

function ClientInsights({
  isFullyIndexed,
  indexingPercentage,
}: {
  isFullyIndexed: boolean;
  indexingPercentage: number;
}) {
  const tGeneral = useTranslations('general');
  const t = useTranslations('pages.learnings');
  const { userMeta } = useContext(AppContext);
  const organizationId = userMeta?.organizationId ?? '';
  const setCreateLearningOpen = useSetAtom(createLearningOpenAtom);

  const { data, isLoading } = useGetLearningsQuery({
    organizationId,
    enabled: !!organizationId,
  });
  const { data: tenderFeedbacks } = useOrganizationTenderFeedbacks(organizationId);
  const clientsData = data?.insights.filter((i) => i.clientId !== null);
  const hasData = data?.insights && data.insights.filter((i) => i.clientId !== null).length > 0;

  return (
    <section>
      <div className="flex justify-between items-center gap-2">
        <H2 className="mb-3 mt-5">{t('section3.title')}</H2>
        {!isFullyIndexed ? (
          <Tooltip>
            <TooltipTrigger
              delay={0}
              render={
                <Button variant="secondary" kind="with-icon" disabled>
                  <HugeiconsIcon icon={PlusSignIcon} />
                  {t('addClient.button')}
                </Button>
              }
            />
            <TooltipPopup>
              {t('indexingTooltip', {
                percentage: indexingPercentage.toFixed(0),
              })}
            </TooltipPopup>
          </Tooltip>
        ) : (
          <Button variant="secondary" kind="with-icon" onClick={() => setCreateLearningOpen(true)}>
            <HugeiconsIcon icon={PlusSignIcon} />
            {t('addClient.button')}
          </Button>
        )}
      </div>
      <MainFrames
        className="not-data-has-data:@7xl:grid-cols-1 not-data-has-data:@3xl:grid-cols-1 data-has-one:@3xl:grid-cols-2"
        data-has-data={hasData ? '' : undefined}
        data-has-one={clientsData && clientsData.length === 1 ? '' : undefined}
      >
        {hasData ? (
          clientsData?.map((item) => {
            const increasingItems = item?.statements.filter(
              (s) => s.changeDirection === 'improvement',
            );
            const decreasingItems = item?.statements.filter(
              (s) => s.changeDirection === 'worsening',
            );
            const done = item.status === SummarizationTopicStatus.DONE;

            return (
              <Frame
                key={item.id}
                render={
                  <Link
                    className={!done ? 'cursor-auto' : ''}
                    to={'/learnings/clients/$clientId'}
                    params={{ clientId: item.clientId ?? '' }}
                    onClick={(e) => {
                      if (!done) return e.preventDefault();
                    }}
                  />
                }
              >
                <FramePanel className="h-38 grid place-items-center">
                  {item.status === SummarizationTopicStatus.IN_PROGRESS ? (
                    <Progress value={+item.percentage.toFixed(0)} max={100} className={'mx-auto'}>
                      <p className="text-muted text-sm mb-2 text-center">
                        <Spinner size="xs" className="inline-block mr-2 mb-0.75" />
                        {t('analyzing')} - <ProgressValue />
                      </p>
                      <ProgressTrack className={'max-w-50 mx-auto'} />
                    </Progress>
                  ) : item.status === SummarizationTopicStatus.FAILED ? (
                    <p className="text-destructive text-sm mb-2 text-center">
                      We could not find any learnings for this client.
                    </p>
                  ) : item.status === SummarizationTopicStatus.NOT_STARTED ? (
                    <p className="text-muted text-sm mb-2 text-center">
                      Search for findings for this client has not started yet.
                    </p>
                  ) : done ? (
                    <div className="tabular-nums">
                      <div className="flex items-center justify-center gap-2">
                        <span className="text-[2.4rem] font-medium block">
                          {item?.statements.length ?? 0}
                        </span>
                        <div className="flex flex-col gap-0.5">
                          <div className="flex items-center gap-1 text-sm leading-tight font-medium">
                            <HugeiconsIcon icon={ChartUpIcon} className="size-4 text-green-11" />{' '}
                            {increasingItems?.length ?? 0}
                            <span className="sr-only">{t('improvement')}</span>
                          </div>
                          <div className="flex items-center gap-1 text-sm leading-tight font-medium">
                            <HugeiconsIcon icon={ChartDownIcon} className="size-4 text-red-11" />{' '}
                            {decreasingItems?.length ?? 0}
                            <span className="sr-only"> {t('worsening')}</span>
                          </div>
                        </div>
                      </div>
                      <p className="text-sm">{t('section2.found')}</p>
                    </div>
                  ) : null}
                </FramePanel>
                <div className="flex max-sm:flex-col p-2 pb-1.25 sm:items-center gap-2 min-h-14.75 md:min-h-15.75">
                  <div>
                    <H3>{item.clientName ?? 'No name client'}</H3>
                    <p className="text-muted mt-0.5 text-sm">
                      {t.rich('section3.description', {
                        clientName: item.clientName ?? 'No name client',
                        count:
                          tenderFeedbacks?.filter((fb) => fb.clientName === item.clientName)
                            .length ?? 0,
                        span: (chunks) => <span className="underline">{chunks}</span>,
                      })}
                    </p>
                  </div>
                  {!done ? null : (
                    <HugeiconsIcon
                      icon={ArrowRight02Icon}
                      className="invisible group-hover:visible size-6 ml-auto"
                    />
                  )}
                </div>
              </Frame>
            );
          })
        ) : (
          <Frame>
            <FramePanel className="@xl:h-90 grid place-items-center">
              <div className="flex flex-col justify-center p-2 pb-1.25 items-center gap-2 pr-3">
                <ClientsIcon />
                <H2>{t('section3.empty.title')}</H2>
                <p className="text-muted text-balance mt-0.5 text-sm text-center">
                  {t('section3.empty.description')}
                </p>
                {!isFullyIndexed ? (
                  <Tooltip>
                    <TooltipTrigger
                      delay={0}
                      render={
                        <Button className="mt-3" variant="secondary" kind="with-icon" disabled>
                          <HugeiconsIcon icon={PlusSignIcon} />
                          {t('section3.empty.button')}
                        </Button>
                      }
                    />
                    <TooltipPopup>
                      {t('indexingTooltip', {
                        percentage: indexingPercentage.toFixed(0),
                      })}
                    </TooltipPopup>
                  </Tooltip>
                ) : (
                  <Button
                    className="mt-3"
                    variant="secondary"
                    kind="with-icon"
                    onClick={() => setCreateLearningOpen(true)}
                  >
                    <HugeiconsIcon icon={PlusSignIcon} />
                    {t('section3.empty.button')}
                  </Button>
                )}
              </div>
            </FramePanel>
          </Frame>
        )}
      </MainFrames>
    </section>
  );
}
